import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import Image from 'next/image';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CampgroundHero from '@/assets/become-a-host/campground/hero.jpg';
import RVHero from '@/assets/become-a-host/rv/hero.jpg';
import StayHero from '@/assets/become-a-host/stay/hero.jpg';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ERentalType } from '@/services/analytics/types';

import styles from './BecomeAHost.module.css';
import { useHostingRVFlow } from './RV/useHostingRVFlow';
import { useHostingStayFlow } from './Stay/useHostingStayFlow';
import { useHostingType } from './useHostingType';

export const Hero = () => {
  const { isMobile } = useBreakpoint();
  const intl = useIntl();
  const { rentalType, isRentalRV, isRentalCampground, isRentalStay } = useHostingType();
  const content = {
    [ERentalType.RV]: {
      title: intl.formatMessage({
        defaultMessage: 'Earn income renting your RV or campervan',
        id: 'OxTSQC',
      }),
      description: intl.formatMessage({
        defaultMessage: 'Sign up for free, host when you want, and get paid every week.',
        id: 'Zhts6P',
      }),
      image: RVHero,
      imageAlt: intl.formatMessage({ defaultMessage: 'RV - Become a host banner', id: 'RrOHlT' }),
    },

    [ERentalType.STAY]: {
      title: intl.formatMessage({
        defaultMessage: 'Increase the income from your outdoor Stay',
        id: 'iuU0Ty',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'Maximizes your visibility to millions of outdoorsy guests and get exclusive host savings and benefits.',
        id: 'oA9mZx',
      }),
      image: StayHero,
      imageAlt: intl.formatMessage({ defaultMessage: 'Stay - Become a host banner', id: '0aNY+8' }),
    },

    [ERentalType.CAMPGROUND]: {
      title: intl.formatMessage({
        defaultMessage:
          'Increase the revenue of your campground on the #1 outdoor travel marketplace',
        id: 'G7Oexd',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'We’ve driven over $2b in transactions for our partners and helped guests enjoy over 8 million days of outdoor travel.',
        id: 'SB3NbI',
      }),
      image: CampgroundHero,
      imageAlt: intl.formatMessage({
        defaultMessage: 'Campground - Become a host banner',
        id: '1vyGKT',
      }),
    },
  };

  const { redirectToHostingRVFlow, loading } = useHostingRVFlow();
  const { redirectToHostingStayFlow } = useHostingStayFlow();

  const handleHostingFlow = () => {
    if (rentalType === ERentalType.RV) {
      redirectToHostingRVFlow();
    } else if (rentalType === ERentalType.STAY) {
      redirectToHostingStayFlow();
    }
  };

  return (
    <section>
      <div className="bg-canvas-100">
        <div
          className={`px-4 py-20 mx-auto text-center ${
            // eslint-disable-next-line no-nested-ternary
            isRentalRV ? 'max-w-[42rem]' : isRentalCampground ? 'max-w-[60rem]' : 'max-w-[50rem]'
          }`}>
          <Text variant={ETextStyleVariant.LegalRegular} className="uppercase">
            {isRentalCampground ? (
              <FormattedMessage defaultMessage="List your campground" id="2RvIth" />
            ) : (
              <FormattedMessage defaultMessage="BECOME A HOST" id="06MJEL" />
            )}
          </Text>
          <Heading
            variant={isMobile ? EHeadingStyleVariant.H2 : EHeadingStyleVariant.H1}
            className="mt-6">
            {content[rentalType].title}
          </Heading>

          <Heading
            variant={!isRentalStay ? EHeadingStyleVariant.H4Alt : EHeadingStyleVariant.H6}
            className={`${isRentalCampground ? 'max-w-[55rem] mt-6 mb-8' : 'mt-4 mb-6'}`}>
            {content[rentalType].description}
          </Heading>

          {isRentalCampground ? (
            <Button
              href="#start-listing-form"
              variant={EButtonColorVariant.Primary}
              label={intl.formatMessage({
                defaultMessage: 'List your campground today',
                id: 'NogPlB',
              })}
            />
          ) : (
            <Button
              onClick={handleHostingFlow}
              variant={EButtonColorVariant.Primary}
              label={intl.formatMessage({ defaultMessage: 'Get started', id: '/aBLH2' })}
              loading={loading}
            />
          )}
        </div>
      </div>

      <div className={styles.heroContainer}>
        <Image
          src={content[rentalType].image}
          alt={content[rentalType].imageAlt}
          role="presentation"
          sizes="100vw"
          priority={true}
          width={1139}
          height={629}
          className="rounded-2xl"
        />
      </div>
    </section>
  );
};
